import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import type { Paged } from "@/shared/types/paged";
import { accessLocationsKey } from "./use-organization-locations";

function idsOfCachedPages(queryClient: QueryClient, organizationId: string) {
  const ids = new Set<string>();
  let pages = 0;
  for (const [, page] of queryClient.getQueriesData<Paged<{ readonly id: string }>>({
    queryKey: accessLocationsKey(organizationId),
  })) {
    if (!page) continue;
    pages += 1;
    for (const item of page.items) ids.add(item.id);
  }
  return { ids, pages };
}

/** Every location id the cached pages of the organization list right now. */
export function cachedLocationIds(
  queryClient: QueryClient,
  organizationId: string,
): ReadonlySet<string> {
  return idsOfCachedPages(queryClient, organizationId).ids;
}

/**
 * After a write was refused because a picked location is gone or moved: the
 * ids that were visible BEFORE the write (`before`, taken at submit) and that
 * the organization no longer lists. The pages already read are re-read first
 * (the stale, inactive ones too: the access refresh only refetches the mounted
 * page). Only ids seen before can be reported, so a tick made on a page or
 * search that has since been evicted from the cache is never dropped on a
 * guess. With nothing cached, or a failed re-read, nothing is reported.
 */
export async function findGoneLocationIds(
  queryClient: QueryClient,
  organizationId: string,
  before: ReadonlySet<string>,
): Promise<ReadonlySet<string>> {
  await queryClient.refetchQueries({
    queryKey: accessLocationsKey(organizationId),
    stale: true,
  });
  const { ids: fresh, pages } = idsOfCachedPages(queryClient, organizationId);
  if (pages === 0) return new Set();
  return new Set([...before].filter((id) => !fresh.has(id)));
}

/** A new set without `gone` (the same set when there is nothing to drop). */
export function withoutIds(
  ids: ReadonlySet<string>,
  gone: ReadonlySet<string>,
): ReadonlySet<string> {
  if (gone.size === 0) return ids;
  return new Set([...ids].filter((id) => !gone.has(id)));
}

export interface GoneLocations {
  /** Call right before the write: remembers which locations were visible. */
  readonly snapshot: () => void;
  /** After a location-gone refusal: the remembered ids the organization no longer lists. */
  readonly find: () => Promise<ReadonlySet<string>>;
}

/** `findGoneLocationIds` bound to the app's query client and to one submit's snapshot. */
export function useGoneLocations(organizationId: string): GoneLocations {
  const queryClient = useQueryClient();
  const before = useRef<ReadonlySet<string>>(new Set());
  return {
    snapshot: () => {
      before.current = cachedLocationIds(queryClient, organizationId);
    },
    find: () => findGoneLocationIds(queryClient, organizationId, before.current),
  };
}
